import { createAsyncThunk, createSlice, isRejected } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { httpGet, httpPost, PAGESIZE } from '../../helpers/http.js';
import { getPlayerTypeLabel, getPlayerType, getPlayerTypeValue } from './helpers.js';

export const loadElearnings = createAsyncThunk(
    'elearning/fetch',
    async ({ filters = {}, reload }, { getState }) => {
        const { elearning: state, env, auth } = getState();

        const portalId = env?.portalIdentifier || '';
        const newPage = reload ? 0 : state.page + 1;
        const params = new URLSearchParams({
            page: newPage,
            pageSize: PAGESIZE,
        });

        const search = filters.search !== undefined ? filters.search : state.filters.search;
        if (search && search !== '') {
            params.set('search', search);
        }

        const playerType = filters.playerType !== undefined ? filters.playerType : state.filters.playerType;
        if (playerType && playerType !== '') {
            params.set('player', parseInt(playerType, 10));
        }

        const supplier = filters.supplier !== undefined ? filters.supplier : state.filters.supplier;
        if (supplier && supplier !== '') {
            params.set('supplier', supplier);
        }

        const route = env.hasGlobalId  ? 'elearningitems' : 'catalog-elearningitems';
        const response = await httpGet(`${env.settings.compositionApi}/${route}/${portalId}?${params}`, auth.user);

        return {
            list: response.data,
            page: newPage,
            total: response.total,
            reload: reload,
            search: search,
            playerType: playerType,
            supplier: supplier,
        };
    },
    {
        condition: ({ index, reload }, { getState }) => {
            const { elearning: state } = getState();
            if (state.status === 'loading') {
                return false;
            }

            if (!reload && (index < state.list.length - 1 || index >= state.total - 1)) {
                return false;
            }
        },
    },
);

export const loadFilters = createAsyncThunk(
    'elearning/fetchFilters',
    async (_, { getState }) => {
        const { env, auth } = getState();

        const portalId = env?.portalIdentifier || '';
        const response = await httpGet(`${env.settings.compositionApi}/elearningitems/filters/${portalId}`, auth.user);

        return {
            isGlobal: env.hasGlobalId,
            playerTypes: response.playerTypes,
            suppliers: response.suppliers,
        };
    },
    {
        condition: ({ reload }, { getState }) => {
            const { elearning: state } = getState();

            if (!reload && state.playerTypes?.length > 0) {
                return false;
            }
        },
    },
);

const elearningSlice = createSlice({
    name: 'elearning',
    initialState: {
        status: 'idle',
        filters: {},
        error: null,
        page: -1,
        current: null,
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadElearnings.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadElearnings.fulfilled, (state, action) => {
                const { reload, list, page, total, search, playerType, supplier } = action.payload;

                state.status = 'success';
                state.list = reload ? [...list] : [...state.list, ...list];
                state.page = page;
                state.total = total;
                state.filters = { search, playerType, supplier };
            })
             .addCase(loadElearning.pending, (state) => {
                    state.status = 'loading';
                })
                .addCase(loadElearning.fulfilled, (state, action) => {
                    const { payload } = action;

                    state.status = 'success';
                    state.current = payload;
                    state.dirty = false;
                })
                .addCase(loadElearning.rejected, (state, action) => {
                    state.status = 'failed';
                    state.error = action.error;
                })
            .addCase(loadFilters.pending, (state) => {
                state.status = 'loadingFilters';
            })
            .addCase(loadFilters.fulfilled, (state, action) => {
                const { isGlobal, playerTypes, suppliers } = action.payload;

                state.status = 'success';
                state.playerTypes = playerTypes.map((v) => ({ value: v + '', label: getPlayerTypeLabel(v, isGlobal)}));
                state.suppliers = suppliers.map((s) => ({ value: s.supplierKey, label: s.name}));
            })
            .addMatcher(
                isRejected(
                    loadElearnings,
                    loadFilters,
                ),
                (state, action) => {
                    state.status = 'failed';
                    state.error = action.error;
                },
            );
    },
});

export default elearningSlice.reducer;

// generic elearningitem loading helpers, thunks are created in the scorm and os slice
export const createLoadElearningItemsThunk = (sliceName, playerType) => {
    return createAsyncThunk(
        `${sliceName}/fetch`,
        async (args, { getState }) => {
            const state = getState();
            const { env, auth } = state;
            const slice = state[sliceName];
            const { search, reload } = args;

            const portalId = env?.portalIdentifier || '';
            const newPage = reload ? 0 : slice.page + 1;
            const params = new URLSearchParams({
                player: playerType,
                page: newPage,
                pageSize: PAGESIZE,
            });

            const searchFor = search || search === '' ? search : slice.search;
            if (searchFor) {
                params.set('search', searchFor);
            }

            const response = await httpGet(`${env.settings.compositionApi}/elearningitems/${portalId}?${params}`, auth.user);

            return {
                list: response.data,
                page: newPage,
                total: response.total,
                reload: reload,
                search: searchFor,
            };
        },
        {
            condition: (args, { getState }) => {
                const state = getState();
                const slice = state[sliceName];

                if (!args.reload && (args.index < slice.list.length - 1 || args.index >= slice.total - 1)) {
                    return false;
                }
            },
        },
    );

};

export const createLoadElearningItemThunk = (sliceName) => {
    return createAsyncThunk(
        `${sliceName}/fetchOne`,
            async (args, { getState }) => {
            const { targetId } = args;
            const { env, auth } = getState();

            return await httpGet(`${env.settings.compositionApi}/elearningitems/${targetId}`, auth.user);
        },
        {
            condition: (args, { getState }) => {
                const state = getState();
                const slice = state[sliceName];

                if (args.targetId === slice.current?.id) {
                    return false;
                }
            },
        },
    );
};

export const loadElearning = createLoadElearningItemThunk('elearning');

export const createPublishAsCompositionThunk = (sliceName) => {
    return createAsyncThunk(
        `${sliceName}/publish`,
        async (args, { getState }) => {
            const { item } = args;
            const { env, auth } = getState();

            const suffix = getPlayerType(item.playerType);

            await httpPost(`${env.settings.compositionApi}/package/${suffix}`, auth.user, {
                portalId: env.portalIdentifier,
                publisherIdString: auth.user.profile.sub,
                publisherName: auth.user.profile.name,
                items: [
                    {
                        targetId: item.targetId,
                        type: 'elearning',
                    }
                ],
                updateMode: 2, // 'none'
                distributionKeys: env.hasGlobalId ? [] : [env.portalIdentifier],
                autoCreateComposition: true,
            });

            return {
                ...item,
                lastModified: new Date(),
                elearningType: 0,
            };
        },
    );
};

export const createUnpublishAsCompositionThunk = (sliceName) => {
    return createAsyncThunk(
        `${sliceName}/unpublish`,
        async (args, { getState }) => {
            const { item } = args;
            const { env, auth } = getState();

            await httpPost(`${env.settings.compositionApi}/unpublish`, auth.user, {
                targetId: item.targetId,
                portalIds: [env.portalIdentifier],
            });

            return {
                ...item,
                lastModified: new Date(),
                elearningType: 1,
            };
        },
    );
};

export const createNewElearningItemThunk = (sliceName, playerType) => {
    return createAsyncThunk(
        `${sliceName}/create`,
        async (args, { getState }) => {
            const { name, supplier, thumbnail, image, startData, autoComplete } = args;
            const { env, auth } = getState();
            const newTargetId = uuidv4();

            await httpPost(`${env.settings.compositionApi}/package/${playerType}`, auth.user, {
                portalId: env.portalIdentifier,
                supplierKey: supplier?.supplierKey,
                supplierName: supplier?.name,
                publisherIdString: auth.user.profile.sub,
                publisherName: auth.user.profile.name,
                items: [
                    {
                        targetId: newTargetId,
                        title: name,
                        type: 'microlearning',
                        image: image,
                        thumbnail: thumbnail,
                        startData: startData,
                        autoComplete: autoComplete,
                    }
                ],
                updateMode: 1, // 'newVersion'
            });

            return {
                targetId: newTargetId,
                title: name,
                description: null,
                playerType: getPlayerTypeValue(playerType), //
                elearningType: 1, // microlearning
                lastModified: new Date(),
                supplier: supplier,
                image: image,
                thumbnail: thumbnail,
            };
        },
    );
};

export const deleteElearningItemThunk = (sliceName) => {
    return createAsyncThunk(
        `${sliceName}/delete`,
        async (args, { getState }) => {
            const { elearningItemId } = args;
            const { env, auth } = getState();

            await httpDelete(`${env.settings.compositionApi}/elearningitem/${elearningItemId}`, auth.user);
            return {
                id: elearningItemId,
            };
        },
    );
};

export const saveElearningItemThunk = (sliceName, playerType) => {
    return createAsyncThunk(
        `${sliceName}/save`,
        async (_, { getState }) => {
            const state = getState();
            const { env, auth } = state;
            const slice = state[sliceName];
            const { current } = slice;
            const {
                targetId,
                title,
                description,
                supplier,
                launchType,
                lastVersion
            } = current;

            const elearningPackage = {
                portalId: env.portalIdentifier,
                supplierKey: supplier.supplierKey,
                supplierName: supplier.name,
                publisherIdString: auth.user.profile.sub,
                publisherName: auth.user.profile.name,
                items: [
                    {
                        targetId: targetId,
                        title: title,
                        description: description,
                        launchType: launchType,
                        startUrl: lastVersion.startUrl,
                        startData: lastVersion.startData,
                        autoComplete: lastVersion.autoComplete,
                    }
                ],
                updateMode: 0, // 'update'
            };

            await httpPost(`${env.settings.compositionApi}/package/${playerType}`, auth.user, elearningPackage);

            return await httpGet(`${env.settings.compositionApi}/elearningitems/${targetId}`, auth.user);
        },
    );
};
